import { useMemo, useRef, useState } from 'react';

import type { RoleName } from '@sae/automation';
import { normalizeAreaId, sampleCsvText, templateCsvText } from '@sae/io';
import type { DataTable } from '@sae/io';

import { fmtPct } from '../domain/format.js';
import type { ProjectState } from '../domain/types.js';
import { downloadData } from './download.js';
import { useProjectState, useStore } from './hooks.js';
import { ROLE_OPTIONS, currentRoleOf } from './rolesUi.js';

interface CoverageCell {
  areaId: string;
  value: number | null;
  display: string;
}

/**
 * Coverage values from the loaded table: the direct estimate per
 * area for an area-level file, respondents per area for microdata.
 * One pass over the table; display only — statistics stay in the
 * engine.
 */
function coverageOf(state: ProjectState): {
  cells: CoverageCell[];
  isEstimate: boolean;
} {
  const table = state.source.table;
  const setup = state.setup;
  if (table === null || setup === null) return { cells: [], isEstimate: false };
  const roles = setup.roles;
  const areaIdx = table.columns.findIndex((c) => c.name === roles.areaId);
  if (areaIdx < 0) return { cells: [], isEstimate: false };
  const kind = setup.assessment.classification.kind;
  if (kind === 'area-level' && roles.estimate !== undefined) {
    const estIdx = table.columns.findIndex((c) => c.name === roles.estimate);
    const cells: CoverageCell[] = [];
    for (const row of table.rows) {
      const area = row[areaIdx];
      if (area === null || area === '') continue;
      const raw = estIdx >= 0 ? row[estIdx] : null;
      const value =
        typeof raw === 'number'
          ? setup.estimateScale === 'percent'
            ? raw / 100
            : raw
          : null;
      cells.push({
        areaId: normalizeAreaId(area),
        value,
        display: value !== null ? fmtPct(value) : '—',
      });
    }
    return { cells, isEstimate: true };
  }
  const counts = new Map<string, number>();
  for (const row of table.rows) {
    const area = row[areaIdx];
    if (area === null || area === '') continue;
    const id = normalizeAreaId(area);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return {
    cells: [...counts.entries()].map(([areaId, n]) => ({
      areaId,
      value: n,
      display: `${n}`,
    })),
    isEstimate: false,
  };
}

/* Coverage shades are CSS classes (styles.css .tile-s0..s3): the
   steps are color-mix derivations of the selected accent, so the
   map follows the appearance system. */
const TILE_CLASSES = ['tile-s0', 'tile-s1', 'tile-s2', 'tile-s3'];

function shadeFor(value: number | null, min: number, max: number): string {
  if (value === null || max <= min) return TILE_CLASSES[0];
  const t = (value - min) / (max - min);
  return TILE_CLASSES[Math.min(3, Math.floor(t * 4))];
}

export function Step1Upload() {
  const store = useStore();
  const state = useProjectState();
  const fileInput = useRef<HTMLInputElement>(null);
  const covInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const setup = state.setup;
  const table: DataTable | null = state.source.table;

  const coverage = useMemo(() => coverageOf(state), [state]);
  const coverageValues = coverage.cells
    .map((c) => c.value)
    .filter((v): v is number => v !== null);
  const covMin = coverageValues.length > 0 ? Math.min(...coverageValues) : 0;
  const covMax = coverageValues.length > 0 ? Math.max(...coverageValues) : 0;

  const onFile = async (file: File | undefined) => {
    if (file === undefined) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    await store.loadFile(bytes, file.name);
  };
  const onCovFile = async (file: File | undefined) => {
    if (file === undefined) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    await store.loadCovariatesFile(bytes, file.name);
  };

  const continueToConfigure = () => {
    store.goToStep('configure');
    void store.refreshQc();
    void store.refreshFeasibility();
  };

  const summary = setup?.assessment.summary;
  const classification = setup?.assessment.classification;
  const kindLabel =
    classification === undefined
      ? null
      : classification.kind === 'microdata'
        ? 'microdata file'
        : classification.kind === 'area-level'
          ? 'area-level file'
          : classification.kind === 'area-covariates'
            ? 'area covariates file'
            : 'file type not certain';

  return (
    <div className="panel active">
      <div className="grid2">
        <div className="card">
          <h2>Start with a File, or Use the Sample Data</h2>
          <p className="sub">
            Two kinds of file are accepted. An <strong>area-level
            file</strong> has one row per area, with the direct estimate
            and its sampling variance already computed. A{' '}
            <strong>microdata file</strong> has one row per respondent, and
            the tool computes the direct estimates and variances. Accepted
            formats: <strong>.csv, .xlsx, .sav (SPSS), .dta (Stata),
            .sas7bdat and .xpt (SAS)</strong>. Column roles are detected
            automatically; you confirm them before anything runs.
          </p>
          <div
            className={`drop${dragOver ? ' drag' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void onFile(e.dataTransfer.files?.[0]);
            }}
          >
            <strong>Drop Your File Here</strong>
            <p>
              Area-level file or respondent-level microdata. The file is
              read in your browser and is never uploaded.
            </p>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept=".csv,.tsv,.xlsx,.sav,.dta,.sas7bdat,.xpt"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <button
              className="btn btn-primary"
              onClick={() => fileInput.current?.click()}
            >
              Choose File
            </button>{' '}
            <button className="btn" onClick={() => void store.loadDemo()}>
              Use Sample Data
            </button>
          </div>

          <div className="reqbox">
            <b>What Your File Must Contain</b>
            <ul className="req-list">
              <li>
                <strong>Area</strong> — the small area each row belongs to
                (county, town, district). One row per area in an area-level
                file. Required.
              </li>
              <li>
                <strong>Respondent-level file</strong> — the outcome
                variable, the survey weight, and the design variables
                (strata and PSU, or replicate weights). The tool computes
                direct estimates and variances for you.
              </li>
              <li>
                <strong>Area-level file</strong> — one row per area: the
                direct estimate, its sampling variance (a standard error
                works too; the tool squares it), and the sample size.
              </li>
              <li>
                <strong>Covariates</strong> — optional area-level
                predictors the model can borrow strength from, in the same
                file or a companion file keyed by the same area codes.
                Population control totals, when available, feed
                benchmarking, which is on by default.
              </li>
            </ul>
            <p className="req-note">
              Compare your file with the{' '}
              <button
                className="linklike"
                onClick={() =>
                  downloadData(
                    'maine_county_poverty_sample.csv',
                    sampleCsvText(),
                    'text/csv',
                  )
                }
              >
                Sample File (.csv)
              </button>{' '}
              or the{' '}
              <button
                className="linklike"
                onClick={() =>
                  downloadData(
                    'sae_area_level_template.csv',
                    templateCsvText(),
                    'text/csv',
                  )
                }
              >
                Blank Template (.csv)
              </button>{' '}
              and match your columns to them.
            </p>
          </div>

          {state.source.filename !== null && (
            <div className="file-row">
              <div>
                <b>{state.source.filename}</b>
                <span>
                  {table !== null
                    ? `${table.rows.length} rows · ${table.columns.length} columns`
                    : 'file not stored'}
                  {kindLabel !== null && ` · Detected: ${kindLabel}`}
                  {state.source.kind === 'demo' && ' · Sample data'}
                </span>
              </div>
              {setup !== null && <span className="file-ok">✓ Profiled</span>}
            </div>
          )}

          {state.source.readIssues.length > 0 && (
            <ul className="issue-list">
              {state.source.readIssues.map((issue, i) => (
                <li key={i} className={issue.severity}>
                  {issue.message}
                </li>
              ))}
            </ul>
          )}

          {summary !== undefined && summary !== null && (
            <div className="kv">
              <div>
                <b>{summary.areasFound ?? '—'}</b>
                <span>Areas Detected</span>
              </div>
              <div>
                <b>{summary.covariateCandidates}</b>
                <span>Covariates Found</span>
              </div>
              <div>
                <b>{summary.designVariablesFound}</b>
                <span>Design Variables Found</span>
              </div>
              <div>
                <b>{summary.indicatorCandidates}</b>
                <span>Indicator Candidates</span>
              </div>
            </div>
          )}

          <div className="cov-file">
            <b>Covariate File (Optional)</b>
            {state.covariatesSource === null ? (
              <p className="sub">
                A separate table keyed by the same area codes, when the
                covariates are not in the main file.{' '}
                <input
                  ref={covInput}
                  type="file"
                  hidden
                  accept=".csv,.tsv,.xlsx,.sav,.dta,.sas7bdat,.xpt"
                  onChange={(e) => void onCovFile(e.target.files?.[0])}
                />
                <button
                  className="linklike"
                  onClick={() => covInput.current?.click()}
                >
                  Add a Covariate File
                </button>
              </p>
            ) : (
              <p className="sub">
                {state.covariatesSource.filename} —{' '}
                {state.covariatesSource.roles?.covariates.length ?? 0}{' '}
                covariates detected.{' '}
                <button
                  className="linklike"
                  onClick={() => store.clearCovariatesFile()}
                >
                  Remove
                </button>
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="card maplike">
            <div className="map-head">
              <b>
                Coverage:{' '}
                {coverage.isEstimate
                  ? 'Direct Estimate by Area'
                  : 'Respondents by Area'}
              </b>
              <span>{coverage.isEstimate ? 'Rate' : 'Respondents'}</span>
            </div>
            {coverage.cells.length === 0 ? (
              <p className="sub pad">
                Load a file and the coverage view shows every area it
                contains.
              </p>
            ) : (
              <>
                <div className="choropleth">
                  {coverage.cells.map((cell) => (
                    <div
                      key={cell.areaId}
                      className={`tile ${shadeFor(cell.value, covMin, covMax)}`}
                      title={`${cell.areaId}: ${cell.display}`}
                    >
                      <b>{cell.areaId}</b>
                      <span>{cell.display}</span>
                    </div>
                  ))}
                </div>
                <div className="legend">
                  <span>
                    <i className={TILE_CLASSES[0]}></i>
                    {coverage.isEstimate
                      ? fmtPct(covMin)
                      : `${Math.round(covMin)}`}
                  </span>
                  <span>
                    <i className={TILE_CLASSES[3]}></i>
                    {coverage.isEstimate
                      ? fmtPct(covMax)
                      : `${Math.round(covMax)}`}
                  </span>
                </div>
              </>
            )}
          </div>

          {setup !== null && table !== null && (
            <div className="card">
              <h2>Column Roles</h2>
              <p className="sub">
                Detection is automatic; confirmation is yours. Anything
                marked for review is a best estimate. Correct it here;
                nothing runs until the roles are confirmed.
              </p>
              <div className="roles-list">
                {table.columns.map((column) => {
                  const decision = setup.assessment.roles.decisions.find(
                    (d) => d.column === column.name,
                  );
                  const current = currentRoleOf(setup.roles, column.name);
                  const needsReview =
                    decision !== undefined &&
                    (decision.status === 'review' || decision.contested);
                  return (
                    <div
                      key={column.name}
                      className={`role-row${needsReview && current === (decision?.role ?? null) ? ' review' : ''}`}
                    >
                      <div className="role-col">
                        <b>{column.name}</b>
                        {column.label !== null && <span>{column.label}</span>}
                      </div>
                      <select
                        aria-label={`Role for ${column.name}`}
                        value={current ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const role =
                            value === '' ? null : (value as RoleName);
                          if (
                            role === 'indicator' &&
                            setup.indicatorRules[column.name] === undefined
                          ) {
                            void store.addIndicatorColumn(column.name);
                          } else {
                            store.assignColumnRole(column.name, role);
                          }
                        }}
                      >
                        <option value="">Unassigned</option>
                        {ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="role-conf">
                        {decision !== undefined && decision.role !== null
                          ? `${Math.round(decision.confidence * 100)}%`
                          : ''}
                        {needsReview && (
                          <em className="review-tag">review</em>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              {classification !== undefined && classification !== null && (
                <p className="sub">
                  Classification: {kindLabel} (
                  {Math.round(classification.confidence * 100)}% confidence).
                  Preset: {setup.assessment.preset.name}.
                </p>
              )}
              <button
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={continueToConfigure}
              >
                Continue to Model Setup →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
